type GtagCommand = 'js' | 'config' | 'event';

type Gtag = (
  command: GtagCommand,
  target: string | Date,
  params?: Record<string, unknown>
) => void;

type WindowWithGoogleAds = Window & {
  gtag?: Gtag;
  dataLayer?: unknown[];
};

const GOOGLE_ADS_ID = 'AW-10886360231';
const GOOGLE_ADS_SIGNUP_SEND_TO = 'AW-10886360231/oVJRCOO81pQcEKfZgsco';
const GOOGLE_ADS_SUBSCRIPTION_SEND_TO = 'AW-10886360231/ID4uCLq2t5YcEKfZgsco';
const GOOGLE_ADS_SUBSCRIPTION_CONVERSION_VALUE = 40.0;

const SUBSCRIPTION_CONVERSION_STORAGE_KEY_PREFIX =
  'ora-google-ads-subscription-conversion:';

const GOOGLE_ADS_SCRIPT_ID = 'ora-google-ads';
let googleAdsScriptPromise: Promise<Gtag | null> | null = null;

const getGoogleAdsTracker = (): Gtag | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const { gtag } = window as WindowWithGoogleAds;
  return typeof gtag === 'function' ? gtag : null;
};

const loadGoogleAdsTracker = async (): Promise<Gtag | null> => {
  if (typeof window === 'undefined') {
    return null;
  }

  const existingGtag = getGoogleAdsTracker();
  if (existingGtag) {
    return existingGtag;
  }

  if (googleAdsScriptPromise) {
    return googleAdsScriptPromise;
  }

  const googleAdsWindow = window as WindowWithGoogleAds;
  googleAdsWindow.dataLayer = googleAdsWindow.dataLayer || [];

  googleAdsWindow.gtag = ((...args) => {
    googleAdsWindow.dataLayer?.push(args);
  }) as Gtag;

  googleAdsWindow.gtag('js', new Date());
  googleAdsWindow.gtag('config', GOOGLE_ADS_ID);

  googleAdsScriptPromise = new Promise<Gtag | null>((resolve) => {
    if (document.getElementById(GOOGLE_ADS_SCRIPT_ID)) {
      resolve(getGoogleAdsTracker());
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_ADS_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
    script.onload = () => resolve(getGoogleAdsTracker());

    script.onerror = () => {
      googleAdsScriptPromise = null;
      resolve(null);
    };

    document.head.appendChild(script);
  });

  return googleAdsScriptPromise;
};

const hasReportedSubscriptionConversion = (subscriptionId: string): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return (
    localStorage.getItem(
      `${SUBSCRIPTION_CONVERSION_STORAGE_KEY_PREFIX}${subscriptionId}`
    ) === '1'
  );
};

const markSubscriptionConversionReported = (subscriptionId: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.setItem(
    `${SUBSCRIPTION_CONVERSION_STORAGE_KEY_PREFIX}${subscriptionId}`,
    '1'
  );
};

export const reportGoogleAdsSignupConversion = async () => {
  const gtag = await loadGoogleAdsTracker();
  if (!gtag) {
    return;
  }

  gtag('event', 'conversion', {
    send_to: GOOGLE_ADS_SIGNUP_SEND_TO,
    value: 5.0,
    currency: 'USD',
  });
};

export const reportGoogleAdsSubscriptionConversion = (params: {
  subscriptionId?: string | null;
}): Promise<'reported' | 'duplicate' | 'skipped'> => {
  const subscriptionId = params.subscriptionId?.trim();
  if (!subscriptionId) {
    return Promise.resolve('skipped');
  }

  if (hasReportedSubscriptionConversion(subscriptionId)) {
    return Promise.resolve('duplicate');
  }

  return loadGoogleAdsTracker().then((gtag) => {
    if (!gtag) {
      return 'skipped';
    }

    gtag('event', 'conversion', {
      send_to: GOOGLE_ADS_SUBSCRIPTION_SEND_TO,
      transaction_id: subscriptionId,
      value: GOOGLE_ADS_SUBSCRIPTION_CONVERSION_VALUE,
      currency: 'USD',
    });

    markSubscriptionConversionReported(subscriptionId);
    return 'reported';
  });
};
